'use client'

import { useState } from 'react'
import GrandTitle from '@/components/GrandTitle'
import Mosaic from '@/components/Mosaic'
import NewsletterModal from '@/components/NewsletterModal'
import SubPageNav from '@/components/SubPageNav'
import { ou, contenuDe, mosaiqueTuiles } from '@/lib/contenuEditable'
import type { SectionConfig } from '@/types/site'

/**
 * Page « Révélation des Étoiles » — mise en page PREMIUM dédiée.
 *
 * Contrairement à la première version, cette page n'emprunte plus la mise en
 * page de « Le Cercle » : elle a la sienne (hero encre marine + constellation,
 * galerie éditoriale, cartes de soutien, bandeau caritatif + passerelle
 * IFCArts, signature). Cela évite aussi la fuite des listes par défaut du Club.
 *
 * Les TEXTES restent pilotés par le CRM via les mêmes sections que le premier
 * jet (couverture / introduction / manifeste / piliers / paris / signature) :
 * chaque valeur absente retombe sur son défaut, la page ne peut pas se vider.
 */

const INK = '#021734'
const CREAM = '#F7EFE1'
const IVORY = '#FBF6EC'
const ACCENT = '#C2A66B'

interface Chapitre {
  eyebrow?: string
  title?: string
  subtitle?: string
  primaryButton?: { label?: string; href?: string }
  secondaryButton?: { label?: string; href?: string }
}
interface Pilier {
  id?: string
  title?: string
  subtitle?: string
  points?: string[]
}

/** Sépare des paragraphes sur les lignes vides. */
function paragraphes(texte: string): string[] {
  return texte.split('\n\n').filter((p) => p.trim() !== '')
}

/** Sur-titre doré centré + filet — standard typographique du site. */
function Eyebrow({ children, onDark = false }: { children: React.ReactNode; onDark?: boolean }) {
  return (
    <div className="text-center">
      <div
        style={{
          fontFamily: 'var(--font-body, inherit)',
          fontSize: 'clamp(11px, 0.6875rem + 0.15vw, 13px)',
          letterSpacing: '0.34em',
          textTransform: 'uppercase',
          fontWeight: 600,
          color: onDark ? ACCENT : `${ACCENT}`,
        }}
      >
        {children}
      </div>
      <div style={{ width: 44, height: 1, background: ACCENT, margin: '16px auto 0', opacity: onDark ? 0.9 : 0.85 }} aria-hidden />
    </div>
  )
}

/** Constellation dorée statique — même motif que « Notre univers ». */
function Constellation() {
  return (
    <svg aria-hidden viewBox="0 0 1200 700" preserveAspectRatio="xMidYMid slice" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
      <g stroke={ACCENT} strokeWidth={0.6} opacity={0.26} fill="none">
        <line x1="160" y1="110" x2="300" y2="180" /><line x1="300" y1="180" x2="410" y2="110" />
        <line x1="880" y1="150" x2="1000" y2="240" /><line x1="1000" y1="240" x2="1080" y2="150" />
        <line x1="200" y1="560" x2="330" y2="500" /><line x1="860" y1="560" x2="980" y2="510" />
      </g>
      <g fill={ACCENT} opacity={0.5}>
        <circle cx="160" cy="110" r="1.8" /><circle cx="300" cy="180" r="2.3" /><circle cx="410" cy="110" r="1.6" />
        <circle cx="880" cy="150" r="1.6" /><circle cx="1000" cy="240" r="2.3" /><circle cx="1080" cy="150" r="1.6" />
        <circle cx="200" cy="560" r="1.8" /><circle cx="330" cy="500" r="2.1" /><circle cx="860" cy="560" r="1.8" /><circle cx="980" cy="510" r="2.1" />
      </g>
      <g fill="#ffffff" opacity={0.22}>
        <circle cx="600" cy="90" r="1" /><circle cx="90" cy="360" r="1" /><circle cx="1130" cy="400" r="1" /><circle cx="640" cy="620" r="1" />
      </g>
    </svg>
  )
}

export default function RevelationContenu({ sections }: { sections: SectionConfig[] }) {
  const [modalOpen, setModalOpen] = useState(false)

  const couverture = contenuDe<{ logoImage?: string; title?: string; titleAlt?: string }>(sections, 'couverture')
  const intro = contenuDe<Chapitre>(sections, 'introduction')
  const manifeste = contenuDe<Chapitre>(sections, 'manifeste')
  const piliersC = contenuDe<{ eyebrow?: string; title?: string; items?: Pilier[] }>(sections, 'piliers')
  const paris = contenuDe<Chapitre>(sections, 'paris')
  const signature = contenuDe<Chapitre>(sections, 'signature')

  const heroEyebrow = ou(intro?.eyebrow, 'Nos Étoiles')
  // Le H1 : le champ « Titre » du CRM (`title`, type hero) le pilote enfin.
  // On garde `titleAlt` comme repli — c'est lui qui portait le titre jusqu'ici,
  // le rendu ne change donc pas tant que « Titre » n'est pas renseigné.
  const heroTitle = ou(couverture?.title, ou(couverture?.titleAlt, 'Révélation des Étoiles'))
  const heroTagline = ou(intro?.title, 'Révéler. Accompagner. Faire briller.')

  const piliers = piliersC?.items && piliersC.items.length > 0 ? piliersC.items : []
  // Galerie « Talents » — visuels du dossier cliente « Révélation des Étoiles »,
  // orientation respectée (tuile portrait → image portrait, tuile large → paysage).
  // 6 emplacements à forme fixe + images par défaut ; chaque image est
  // remplaçable via l'Éditeur (section « moments » → tuile1…tuile6).
  const momentsRevel = contenuDe<Record<string, unknown>>(sections, 'moments')
  const galerie = mosaiqueTuiles(momentsRevel, [
    { src: '/media/revelation/portrait-1.jpg', o: 'portrait' }, // 1 — grande tuile (portrait)
    { src: '/media/revelation/paysage-1.jpg', o: 'paysage' },   // 2 — large (paysage)
    { src: '/media/revelation/portrait-2.jpg', o: 'portrait' }, // 3 — portrait
    { src: '/media/revelation/portrait-3.jpg', o: 'portrait' }, // 4 — portrait
    { src: '/media/revelation/paysage-2.jpg', o: 'paysage' },   // 5 — large (paysage)
    { src: '/media/revelation/paysage-3.jpg', o: 'paysage' },   // 6 — paysage (mobile)
  ])

  return (
    <>
      <SubPageNav />

      <main style={{ backgroundColor: CREAM, color: INK, fontFamily: 'var(--font-body, inherit)' }}>
        {/* ─── HERO ─────────────────────────────────────────── */}
        <section
          className="relative overflow-hidden flex items-center justify-center text-center"
          style={{
            minHeight: 'clamp(280px, 42vh, 460px)',
            color: '#fff',
            background: 'radial-gradient(ellipse 70% 66% at 50% 42%, #0a2247, #04173a 60%, #021734 100%)',
          }}
        >
          <Constellation />
          <div className="relative px-6" style={{ zIndex: 2 }}>
            <Eyebrow onDark>{heroEyebrow}</Eyebrow>
            <GrandTitle as="h1" dark style={{ maxWidth: '92vw', margin: '22px 0 0', textShadow: '0 2px 30px rgba(0,0,0,0.4)' }}>
              {heroTitle}
            </GrandTitle>
            <p style={{ fontFamily: 'var(--font-heading, inherit)', fontStyle: 'italic', fontSize: 'clamp(19px, 2.4vw, 28px)', color: '#e9e0cd', marginTop: 18 }}>
              {heroTagline}
            </p>
          </div>
        </section>

        {/* ─── LE PROJET ────────────────────────────────────── */}
        <section style={{ padding: 'clamp(78px, 9vw, 120px) 24px' }}>
          <div className="mx-auto text-center" style={{ maxWidth: 760 }}>
            <Eyebrow>Le projet</Eyebrow>
            <div style={{ marginTop: 30 }}>
              {paragraphes(ou(intro?.subtitle, "Révélation des Étoiles ouvre des portes, donne des clés, puis accompagne celles et ceux qui les saisissent — pour qu'ils puissent briller.")).map((p, i) => (
                <p
                  key={i}
                  className="font-serif"
                  style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 'clamp(21px, 2.4vw, 30px)', lineHeight: 1.4, fontWeight: 400, color: 'rgba(22,36,58,0.9)', marginTop: i === 0 ? 0 : 18 }}
                >
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* ─── ÉTOILES EN LUMIÈRE (galerie éditoriale) ──────── */}
        <section style={{ backgroundColor: IVORY, padding: 'clamp(78px, 9vw, 120px) 24px' }}>
          <div className="mx-auto text-center" style={{ maxWidth: 1080 }}>
            {/* Sur-titre de la mosaïque : `moments.eyebrow` d'abord (comme sur
                Notre Histoire et Arts & Mode), `manifeste.eyebrow` en repli pour
                préserver l'affichage tant que le champ n'est pas renseigné. */}
            <Eyebrow>{ou(momentsRevel?.eyebrow, ou(manifeste?.eyebrow, 'Étoiles en lumière'))}</Eyebrow>
            <p style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 'clamp(26px, 3.2vw, 40px)', lineHeight: 1.28, fontWeight: 400, maxWidth: 760, margin: '28px auto 20px' }}>
              {ou(manifeste?.title, "Voilà ce qui devient possible lorsque les bonnes portes s'ouvrent.")}
            </p>
            <p style={{ fontSize: 16, lineHeight: 1.85, color: 'rgba(22,36,58,0.78)', maxWidth: 640, margin: '0 auto' }}>
              {ou(manifeste?.subtitle, 'Des trajectoires, des portraits, des couvertures, des affiches et des moments forts : des parcours réels, et l’élan qui les a rendus possibles.')}
            </p>

            <Mosaic images={galerie} uid="revel" />
          </div>
        </section>

        {/* ─── FAIRE BRILLER UNE ÉTOILE (cartes) ────────────── */}
        <section style={{ padding: 'clamp(78px, 9vw, 120px) 24px' }}>
          <div className="mx-auto text-center" style={{ maxWidth: 1080 }}>
            <Eyebrow>{ou(piliersC?.eyebrow, 'Faire briller une Étoile')}</Eyebrow>
            <p style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 'clamp(26px, 3.2vw, 40px)', lineHeight: 1.28, fontWeight: 400, margin: '28px auto 0' }}>
              {ou(piliersC?.title, 'Quatre manières de soutenir un talent.')}
            </p>
            <div className="revel-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20, marginTop: 44, textAlign: 'left' }}>
              {piliers.map((p, i) => (
                <div key={p.id ?? i} className="revel-card" style={{ backgroundColor: IVORY, border: '1px solid rgba(22,36,58,0.1)', padding: '38px 34px' }}>
                  <h3 style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 28, fontWeight: 500, marginBottom: 12 }}>{p.title}</h3>
                  {p.subtitle && <p style={{ fontSize: 14.5, lineHeight: 1.7, color: 'rgba(22,36,58,0.72)', marginBottom: 18 }}>{p.subtitle}</p>}
                  {p.points && p.points.length > 0 && (
                    <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9 }}>
                      {p.points.map((pt, j) => (
                        <li key={j} style={{ display: 'flex', gap: 11, alignItems: 'center', fontSize: 13.5, color: 'rgba(22,36,58,0.8)' }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: ACCENT, flex: 'none' }} aria-hidden />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── DIMENSION CARITATIVE + PASSERELLE IFCArts ────── */}
        <section style={{ backgroundColor: INK, color: '#fff', padding: 'clamp(80px, 10vw, 128px) 24px', textAlign: 'center' }}>
          <div className="mx-auto" style={{ maxWidth: 760 }}>
            <Eyebrow onDark>{ou(paris?.eyebrow, 'La dimension caritative')}</Eyebrow>
            <p style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 'clamp(26px, 3.4vw, 40px)', lineHeight: 1.28, fontWeight: 400, margin: '28px 0 22px' }}>
              {ou(paris?.title, 'Une cause portée par le Gala des Étoiles.')}
            </p>
            <div style={{ maxWidth: 640, margin: '0 auto' }}>
              {paragraphes(ou(paris?.subtitle, 'À chaque édition, le Gala met sa lumière au service de Révélation des Étoiles.')).map((p, i) => (
                <p key={i} style={{ fontSize: 16, lineHeight: 1.85, color: 'rgba(255,255,255,0.72)', marginTop: i === 0 ? 0 : 16 }}>{p}</p>
              ))}
            </div>
            <div className="revel-btns" style={{ marginTop: 34, display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href={ou(paris?.primaryButton?.href, 'https://ifcarts.com')} target="_blank" rel="noreferrer" className="gde-btn gde-btn--solid">
                {ou(paris?.primaryButton?.label, 'Découvrir IFCArts')}
              </a>
              <a href={ou(paris?.secondaryButton?.href, 'https://ifcarts.com')} target="_blank" rel="noreferrer" className="gde-btn gde-btn--on-dark">
                {ou(paris?.secondaryButton?.label, 'Candidater')}
              </a>
            </div>
          </div>
        </section>

        {/* ─── SIGNATURE ────────────────────────────────────── */}
        <section style={{ padding: 'clamp(78px, 9vw, 120px) 24px', textAlign: 'center' }}>
          <div style={{ width: 44, height: 1, background: ACCENT, margin: '0 auto 30px' }} aria-hidden />
          <p style={{ fontFamily: 'var(--font-heading, inherit)', fontStyle: 'italic', fontSize: 'clamp(30px, 4.4vw, 54px)', fontWeight: 400, lineHeight: 1.2, marginBottom: 34 }}>
            {ou(signature?.title, 'Faire de chaque talent une étoile.')}
          </p>
          <div className="revel-btns" style={{ display: 'flex', gap: 18, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
            <a href={ou(signature?.primaryButton?.href, '/devenir-membre')} className="gde-btn">
              {ou(signature?.primaryButton?.label, 'Soutenir une Étoile')}
            </a>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="revel-linkbtn"
              style={{ fontFamily: 'var(--font-heading, inherit)', fontStyle: 'italic', fontSize: 19, color: INK, borderBottom: `1px solid ${ACCENT}`, paddingBottom: 3, background: 'none', cursor: 'pointer' }}
            >
              {ou(signature?.secondaryButton?.label, 'Rester informé')}
            </button>
          </div>
        </section>
      </main>

      <style>{`
        @media (max-width: 820px) {
          /* 4 cartes en 2×2 sur mobile + titres/paddings réduits pour rester nets */
          .revel-cards { grid-template-columns: repeat(2, 1fr) !important; gap: 12px !important; margin-top: 32px !important; }
          .revel-card { padding: 20px 16px !important; }
          .revel-card h3 { font-size: 18px !important; line-height: 1.2 !important; margin-bottom: 8px !important; }
          .revel-card p { font-size: 12.5px !important; line-height: 1.55 !important; margin-bottom: 10px !important; }
          .revel-card li { font-size: 12px !important; }
          /* Boutons des sections passerelle / signature : sur une seule ligne, labels enroulés */
          .revel-btns { flex-wrap: nowrap !important; gap: 10px !important; }
          .revel-btns > a, .revel-btns > button { flex: 1 1 0 !important; min-width: 0 !important; }
          .revel-btns .gde-btn { white-space: normal !important; padding: 11px 10px !important; font-size: 10.5px !important; letter-spacing: 0.08em !important; line-height: 1.35 !important; }
          .revel-linkbtn { font-size: 14px !important; }
        }
      `}</style>

      <NewsletterModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  )
}
